/**
 * useRegionSearch
 *
 * Autocomplete search using the Mapbox Geocoding API.
 * Returns a list of suggested place results.
 */
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { debounce } from '@/lib/utils';
import { useMemo, useState } from 'react';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

async function geocodeQuery(query) {
  if (!query || query.length < 2) return [];
  const { data } = await axios.get(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`,
    {
      params: {
        access_token: MAPBOX_TOKEN,
        types: 'country,region,place,district',
        limit: 6,
      },
    }
  );
  return data.features.map((f) => ({
    id: f.id,
    name: f.place_name,
    center: f.center, // [lng, lat]
    bbox: f.bbox || null,
  }));
}

export function useRegionSearch() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const debouncedSet = useMemo(
    () => debounce((q) => setDebouncedQuery(q), 350),
    []
  );

  const handleChange = (value) => {
    setQuery(value);
    debouncedSet(value);
  };

  const { data: suggestions = [], isFetching } = useQuery({
    queryKey: ['geocode', debouncedQuery],
    queryFn: () => geocodeQuery(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 60_000,
  });

  return { query, handleChange, suggestions, isFetching };
}
